import { lightTheme } from "../../../theme/theme";
type Theme = typeof lightTheme;

export const CategoryCardStyles = (theme: Theme) => {
  const muted = theme.muted ?? "rgba(0,0,0,.5)";
  const isDark = theme.background === "#111" || theme.background === "#0f0f0f";

  return {
    linkReset: {
      textDecoration: "none",
      color: "inherit",
      display: "block",
    } as const,

    // ── Outer card wrapper ──────────────────────────────────────────
    card: {
      width: "100%",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      alignItems: "center",
      gap: 0,
      borderTop: `1px solid ${theme.border ?? "rgba(0,0,0,0.07)"}`,
      paddingTop: 56,
      paddingBottom: 56,
      position: "relative" as const,
    } as const,

    cardReverse: {
      gridTemplateAreas: `"media text"`,
    } as const,

    cardDefault: {
      gridTemplateAreas: `"text media"`,
    } as const,

    // ── Text column ─────────────────────────────────────────────────
    textCol: {
      gridArea: "text",
      display: "flex",
      flexDirection: "column" as const,
      gap: 14,
      padding: "0 48px",
      position: "relative" as const,
    } as const,

    // Faded index number behind content
    indexNumber: {
      position: "absolute" as const,
      top: -24,
      insetInlineStart: 36,
      fontSize: 96,
      fontWeight: 900,
      lineHeight: 1,
      color: theme.text2,
      opacity: isDark ? 0.08 : 0.06,
      pointerEvents: "none" as const,
      userSelect: "none" as const,
      fontFamily: "'Poppins', sans-serif",
      zIndex: 0,
    } as const,

    textInner: {
      position: "relative" as const,
      zIndex: 1,
      display: "flex",
      flexDirection: "column" as const,
      gap: 14,
      borderInlineStart: `3px solid ${theme.primary}`,
      paddingInlineStart: 20,
    } as const,

    // Eyebrow / subtitle
    subtitle: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase" as const,
      color: theme.primary4,
      margin: 0,
    } as const,

    title: {
      margin: 0,
      fontWeight: 800,
      fontSize: "clamp(20px, 2.2vw, 30px)",
      lineHeight: 1.2,
      color: theme.text,
      letterSpacing: "-0.01em",
      fontFamily: "'Poppins', sans-serif",
    } as const,

    desc: {
      margin: 0,
      fontSize: 14,
      lineHeight: 1.8,
      color: muted,
      maxWidth: "52ch",
    } as const,

    pointsList: {
      margin: 0,
      paddingInlineStart: 18,
      display: "grid",
      gap: 6,
      fontSize: 14,
      lineHeight: 1.7,
      color: muted,
    } as const,

    // ── CTA button ──────────────────────────────────────────────────
    ctaWrap: {
      marginTop: 6,
    } as const,

    cta: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 600,
      fontFamily: "'Poppins', sans-serif",
      letterSpacing: "0.04em",
      padding: "10px 20px",
      borderRadius: 2,
      background: theme.primary,
      color: "#fff",
      border: "none",
      cursor: "pointer",
      transition: "opacity .2s ease, transform .2s ease",
    } as const,

    ctaArrow: {
      fontSize: 16,
      lineHeight: 1,
      display: "inline-block",
      transition: "transform .2s ease",
    } as const,

    // ── Media column ────────────────────────────────────────────────
    mediaCol: {
      gridArea: "media",
      width: "100%",
      aspectRatio: "16 / 10",
      overflow: "hidden",
      borderRadius: 4,
      boxShadow: isDark
        ? "0 8px 32px rgba(0,0,0,0.45)"
        : "0 8px 32px rgba(0,0,0,0.10)",
      position: "relative" as const,
    } as const,

    image: {
      width: "100%",
      height: "100%",
      display: "block",
      objectFit: "cover" as const,
      transition: "transform .5s ease",
    } as const,

    // ── Mobile ──────────────────────────────────────────────────────
    cardSm: {
      gridTemplateColumns: "1fr",
      gridTemplateAreas: `"media" "text"`,
      paddingTop: 36,
      paddingBottom: 36,
      gap: 24,
    } as const,

    textColSm: {
      padding: "0 4px",
    } as const,
  } as const;
};
